/**
 *  @swagger
 *  definitions:
 *    RegattaEntryRequirementParams:
 *      type: object
 *      properties:
 *        RegattaEntryId:
 *          description: ID of the associated RegattaEntry
 *          type: string
 *        RegattaRequirementId:
 *          description: ID of the associated RegattaRequirement
 *          type: string
 *        Value:
 *          description: Value of the RegattaEntryRequirement
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryRequirementCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryRequirementParams'
 *        - type: object
 *          required:
 *            - RegattaEntryId
 *            - RegattaRequirementId
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryRequirementQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaEntryRequirement
 *              type: string
 *        - $ref: '#/definitions/RegattaEntryRequirementParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryRequirementUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryRequirementQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryRequirement:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryRequirementCreateParams'
 *        - $ref: '#/definitions/RegattaEntryRequirementUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaEntryRequirement:
 *        allOf:
 *          - $ref: '#/definitions/RegattaEntryRequirement'
 *              RegattaEntry:
 *                description: RegattaEntry of the RegattaEntryRequirement
 *                $ref: '#/components/schemas/RegattaEntry'
 *              RegattaRequirement:
 *                description: RegattaRequirement of the RegattaEntryRequirement
 *                $ref: '#/components/schemas/RegattaRequirement'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaEntryId: string;
  RegattaRequirementId: string;
};

export type $Params = {
  Value: null | string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaEntry?: $Model<'RegattaEntry'>;
  RegattaRequirement?: $Model<'RegattaRequirement'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Value: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaEntryRequirement'> => {
  const RegattaEntryRequirement = stack.define(
    'RegattaEntryRequirement',
    fields(Sequelize),
    {
      tableName: 'RegattaEntryRequirements',
    },
  );

  RegattaEntryRequirement.associate = (models: $ModelClasses) => {
    models.RegattaEntryRequirement.belongsTo(
      models.RegattaEntry,
      {
        as: 'RegattaEntry',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.RegattaEntryRequirement.belongsTo(
      models.RegattaRequirement,
      {
        as: 'RegattaRequirement',
        foreignKey: 'RegattaRequirementId',
      },
    );
  };

  RegattaEntryRequirement.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaEntryRequirement;
});
